import { observer } from 'mobx-react-lite'
import { useCallback } from 'react'
import { useInjection } from '@core/providers/inversify-react.provider'
import { useDismissableSurface } from '@ui-kit/dismissable-surface'
import { DialogPresenter } from './dialog.presenter'

// Mounted once, alongside NotificationsComponent/RouteProgressBarComponent —
// renders whatever DialogPresenter.confirm() is currently waiting on, or
// nothing.
export const DialogComponent = observer(() => {
  const presenter: DialogPresenter = useInjection(DialogPresenter)
  const vm = presenter.viewModel

  // Escape and the backdrop mean the same thing as Cancel: the question was
  // asked, and declining is the safe answer to every confirm in this app.
  const dismiss = useCallback(() => presenter.resolve(false), [presenter])
  // Trapped, because this one really is modal: it says aria-modal="true", and
  // until now a keyboard user could tab straight out of it into the page behind
  // — the attribute was a promise the markup did not keep.
  const surfaceRef = useDismissableSurface({ active: vm.open, onDismiss: dismiss, trapFocus: true })

  if (!vm.open) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/30" onClick={dismiss} aria-hidden="true" />
      <div
        ref={surfaceRef}
        role="alertdialog"
        aria-modal="true"
        aria-describedby="dialog-message"
        tabIndex={-1}
        className="relative w-full max-w-sm rounded-md border border-shell-border bg-shell-surface p-4 shadow-lg"
      >
        <p id="dialog-message" className="text-sm text-shell-ink">
          {vm.message}
        </p>
        <div className="mt-4 flex justify-end gap-x-2">
          <button
            type="button"
            onClick={() => presenter.resolve(false)}
            className="rounded-md border border-shell-border bg-shell-surface px-3 py-1.5 text-sm font-medium text-shell-ink-secondary hover:bg-shell-surface-muted"
          >
            {vm.cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => presenter.resolve(true)}
            className="rounded-md bg-shell-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            {vm.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
})
